import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

// Small dot + expanding ring. Expands when hovering [data-cursor="hover"] or interactive els.
export const CustomCursor = () => {
    const x = useMotionValue(-100);
    const y = useMotionValue(-100);
    const sx = useSpring(x, { stiffness: 500, damping: 40, mass: 0.3 });
    const sy = useSpring(y, { stiffness: 500, damping: 40, mass: 0.3 });

    const [hovering, setHovering] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Disable on coarse pointer / touch
        const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
        if (!mq.matches) return undefined;

        const move = (e) => {
            x.set(e.clientX);
            y.set(e.clientY);
            if (!visible) setVisible(true);
        };
        const down = () => setPlaying(true);
        const up = () => setPlaying(false);
        const leave = () => setVisible(false);
        const enter = () => setVisible(true);

        const over = (e) => {
            const el = e.target;
            if (!(el instanceof Element)) return;
            const interactive = el.closest(
                'a, button, [role="button"], input, textarea, select, [data-cursor="hover"], [data-cursor="play"]'
            );
            setHovering(!!interactive);
        };

        window.addEventListener("mousemove", move, { passive: true });
        window.addEventListener("mousedown", down);
        window.addEventListener("mouseup", up);
        window.addEventListener("mouseleave", leave);
        window.addEventListener("mouseenter", enter);
        window.addEventListener("mouseover", over);

        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mousedown", down);
            window.removeEventListener("mouseup", up);
            window.removeEventListener("mouseleave", leave);
            window.removeEventListener("mouseenter", enter);
            window.removeEventListener("mouseover", over);
        };
    }, [x, y, visible]);

    return (
        <>
            {/* Outer ring */}
            <motion.div
                data-testid="custom-cursor-ring"
                style={{ x: sx, y: sy }}
                className="pointer-events-none fixed left-0 top-0 z-[90] hidden md:block"
                animate={{
                    width: hovering ? 80 : 36,
                    height: hovering ? 80 : 36,
                    borderColor: "#ffffff",
                    opacity: visible ? 1 : 0,
                    scale: playing ? 0.85 : 1
                }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
                <div
                    className="rounded-full border"
                    style={{
                        width: "100%",
                        height: "100%",
                        borderColor: "#ffffff",
                        transform: "translate(-50%, -50%)",
                        mixBlendMode: "difference"
                    }}
                />
            </motion.div>

            {/* Center dot */}
            <motion.div
                data-testid="custom-cursor-dot"
                style={{ x, y }}
                className="pointer-events-none fixed left-0 top-0 z-[91] hidden md:block"
                animate={{ opacity: visible && !hovering ? 1 : 0 }}
                transition={{ duration: 0.2 }}
            >
                <div
                    className="rounded-full bg-white"
                    style={{
                        width: 6,
                        height: 6,
                        transform: "translate(-50%, -50%)",
                        mixBlendMode: "difference"
                    }}
                />
            </motion.div>
        </>
    );
};

export default CustomCursor;
